import hasPermission from "../../utils/hasMultiplePermission";

export const REPORT_PERMISSIONS = {
  VIEW: "reportsview",
  ADD: "reportsadd",
  EDIT: "reportsedit",
  DELETE: "reportsdelete",
  NON_ASSIGNED_VIEW: "reportsnonassignedview",
};

/**
 * @param {Object|Array} givenPermissions - Permissions from redux.
 * @param {string} scope - Permission key to check.
 * @returns {boolean}
 */
export const hasReportPermission = (givenPermissions, scope) =>
  hasPermission({
    scopes: [scope],
    permissions: givenPermissions,
  });

/**
 * User can bypass report user/role assignment checks.
 * @param {Object|Array} givenPermissions
 * @returns {boolean}
 */
export const canViewNonAssignedReports = (givenPermissions) =>
  hasReportPermission(givenPermissions, REPORT_PERMISSIONS.NON_ASSIGNED_VIEW);

/**
 * Redux permissions are loaded and safe to use for report access checks.
 * Matches the loading guard used by `usePermissionGuard`.
 * @param {Object|Array} givenPermissions
 * @returns {boolean}
 */
export const areReportPermissionsReady = (givenPermissions) => {
  const permissionsObject = givenPermissions?.givenPermission ?? givenPermissions;

  return Boolean(
    permissionsObject &&
      typeof permissionsObject === "object" &&
      Object.keys(permissionsObject).length > 0
  );
};

/**
 * @param {Object} user - Logged-in user from `/me`.
 * @returns {{ userId: number|null, roleId: number|null }}
 */
export const getLoggedInReportUserContext = (user = {}) => ({
  userId: Number(user.user_id ?? user.UserID ?? user.id) || null,
  roleId: Number(user.role_id ?? user.roleId) || null,
});

const normalizeReportUserId = (user) => {
  if (user == null || user === "") {
    return null;
  }

  if (typeof user === "object") {
    return Number(user.user_id ?? user.UserID ?? user.value ?? user.id) || null;
  }

  return Number(user) || null;
};

const normalizeReportRoleId = (role) => {
  if (role == null || role === "") {
    return null;
  }

  if (typeof role === "object") {
    return Number(role.role_id ?? role.value ?? role.id) || null;
  }

  return Number(role) || null;
};

/**
 * Checks whether the logged-in user or their role is assigned to a report.
 * Supports both list API objects ({ value, label, user_id }) and detail API id arrays.
 * @param {Object} report - Report row/details from API.
 * @param {number|null} userId
 * @param {number|null} roleId
 * @returns {boolean}
 */
export const isUserAssignedToReport = (report, userId, roleId) => {
  if (!report) {
    return false;
  }

  const users = Array.isArray(report.users) ? report.users : [];
  const roles = Array.isArray(report.roles) ? report.roles : [];
  const normalizedUserId = Number(userId) || null;
  const normalizedRoleId = Number(roleId) || null;

  const isUserMatch =
    normalizedUserId &&
    users.some(
      (user) => normalizeReportUserId(user) === normalizedUserId
    );

  const isRoleMatch =
    normalizedRoleId &&
    roles.some(
      (role) => normalizeReportRoleId(role) === normalizedRoleId
    );

  return Boolean(isUserMatch || isRoleMatch);
};

/**
 * Determines if the current user can perform an action on a report.
 * With `reportsnonassignedview`, only module permissions apply.
 * Without it, the user/role must be assigned first, then module permissions apply.
 * @param {Object} params
 * @param {Object} params.report
 * @param {Object|Array} params.givenPermissions
 * @param {number|null} params.userId
 * @param {number|null} params.roleId
 * @param {"view"|"add"|"edit"|"delete"} [params.action="view"]
 * @returns {boolean}
 */
export const canAccessReport = ({
  report,
  givenPermissions,
  userId,
  roleId,
  action = "view",
}) => {
  const permissionByAction = {
    view: REPORT_PERMISSIONS.VIEW,
    add: REPORT_PERMISSIONS.ADD,
    edit: REPORT_PERMISSIONS.EDIT,
    delete: REPORT_PERMISSIONS.DELETE,
  };

  const requiredPermission = permissionByAction[action];

  if (!hasReportPermission(givenPermissions, requiredPermission)) {
    return false;
  }

  if (canViewNonAssignedReports(givenPermissions)) {
    return true;
  }

  return isUserAssignedToReport(report, userId, roleId);
};

/**
 * Filters report rows visible in list/export.
 * - With `reportsnonassignedview`: all reports remain visible.
 * - Without it: only reports assigned to the logged-in user or role,
 *   including Global brand reports when explicitly assigned.
 * @param {Array<Object>} reports
 * @param {Object|Array} givenPermissions
 * @param {number|null} userId
 * @param {number|null} roleId
 * @returns {Array<Object>}
 */
export const filterReportsByAccess = (
  reports = [],
  givenPermissions,
  userId,
  roleId
) => {
  if (canViewNonAssignedReports(givenPermissions)) {
    return reports;
  }

  if (!hasReportPermission(givenPermissions, REPORT_PERMISSIONS.VIEW)) {
    return [];
  }

  return reports.filter((report) =>
    isUserAssignedToReport(report, userId, roleId)
  );
};
